import { useEffect, useRef } from 'react'
import { useTecladoCobrindo } from './teclado'
import type { ReactNode } from 'react'

type Props = {
  /** O que o leitor de tela anuncia ao abrir. */
  titulo: string
  onFechar: () => void
  children: ReactNode
}

/**
 * Uma folha que sobe de baixo.
 *
 * ## Por que folha, e não popover ancorado
 *
 * Ancorar num controle que pode estar em qualquer altura de uma lista rolada
 * exige medir espaço e virar para cima quando não cabe. A folha aparece sempre
 * no mesmo lugar, e num celular o polegar já está lá. É também o idioma que o
 * iPhone usa para a mesma coisa.
 *
 * ## Extraída de `MenuDoItem` (decisão 230)
 *
 * O menu de item dos lembretes já era exatamente isto. Escrever a segunda cópia
 * para o cadastro de produto daria duas folhas com o mesmo desenho e dois
 * comportamentos de Escape, de fundo e de foco — e a que ninguém tocasse
 * ficaria para trás em silêncio.
 *
 * ## Rola por dentro, com teto
 *
 * O formulário de produto tem oito campos e não cabe em tela pequena. `max-h`
 * mais `overflow-y-auto` deixam a folha rolar sem levar a página junto; sem o
 * teto, ela cresceria para fora da viewport e o botão de salvar ficaria
 * inalcançável — que é o jeito clássico de uma folha quebrar no celular.
 */
export function FolhaDeBaixo({ titulo, onFechar, children }: Props) {
  const painel = useRef<HTMLDivElement>(null)
  const tecladoCobrindo = useTecladoCobrindo()

  // Escape fecha. Sem isso, uma folha aberta por engano com o teclado vira um
  // beco: ela cobre a tela e nada devolve o foco.
  useEffect(() => {
    function aoTeclar(e: KeyboardEvent) {
      if (e.key === 'Escape') onFechar()
    }
    document.addEventListener('keydown', aoTeclar)
    return () => {
      document.removeEventListener('keydown', aoTeclar)
    }
  }, [onFechar])

  // O foco entra na folha, senão o Tab continua percorrendo a página atrás
  // dela — que está coberta e não deveria receber nada.
  //
  // **Quem a monta pode escolher onde o foco cai**, com `data-foco-inicial`.
  // O padrão é o primeiro controle, e na folha de confirmar a nota o primeiro
  // era "Confirmar": Enter no botão da página e Enter de novo gravavam, e a
  // guarda de dois toques virava uma tecla (decisão 307). Uma folha que grava
  // algo irreversível aponta o foco para a saída.
  useEffect(() => {
    const escolhido = painel.current?.querySelector<HTMLElement>('[data-foco-inicial]')
    const primeiro = painel.current?.querySelector<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    )
    ;(escolhido ?? primeiro ?? painel.current)?.focus()
  }, [])

  return (
    <div
      // No desktop ela centra: uma folha presa à borda de baixo de uma tela de
      // 1440px é um gesto de celular sem o polegar que o justifica.
      className="fixed inset-0 z-50 flex items-end justify-center lg:items-center lg:p-6"
      // Sobe com o teclado, pelo mesmo motivo do painel de tela cheia: no iOS
      // ele não encolhe o layout viewport, e `85dvh` também mede a janela
      // inteira — o fim da folha, onde mora o "Salvar", ficava por baixo.
      style={{ bottom: tecladoCobrindo }}
    >
      {/* O fundo fecha ao toque. `aria-hidden` porque o botão "Fechar" da folha
          já é o caminho anunciado — dois rótulos para a mesma saída confundem
          quem ouve a tela. */}
      <button
        type="button"
        aria-hidden="true"
        tabIndex={-1}
        onClick={onFechar}
        className="absolute inset-0 bg-black/60"
      />

      <div
        ref={painel}
        role="dialog"
        aria-modal="true"
        aria-label={titulo}
        tabIndex={-1}
        className="relative m-2 max-h-[85dvh] w-full max-w-md overflow-y-auto rounded-2xl border border-[var(--border-glass)] bg-[var(--surface)] p-3 shadow-2xl"
      >
        {children}
      </div>
    </div>
  )
}
